import React, { FC, useEffect, useRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { CircularProgress, ListItem } from "@mui/material";
import Divider from "@mui/material/Divider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import { styled } from "@mui/system";

import { useNavigate } from "react-router-dom"; 
import IOPDrawerWrapper from "../IOPDrawerWrapper/IOPDrawerWrapper";
import UserLink from "../UserLink/UserLink";
import { connect } from "react-redux";
import { Scrollbars } from "react-custom-scrollbars";
import { fetchMenuAction, setActiveTrailAction } from "../../redux/actions";
import { getUnreadSosCount } from "../../api/api"; 
import theme from "../../theme";

interface Props {
    activeMenuItem: string;
    menu: MainMenuItemInterface[];
    fetchMenu: any;
    setActiveTrail: any;
}

const IOPDrawer: FC<Props> = ({
    activeMenuItem,
    menu,
    fetchMenu,
    setActiveTrail,
}) => {
    const [open, setOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const prevCountRef = useRef<number>(0);

    const navigate = useNavigate(); 

    const handleDrawerToggle = (state: boolean) => {
        setOpen(state);
    };

    useEffect(() => {
        fetchMenu();
    }, [fetchMenu]);

    useEffect(() => {
        const load = async () => {
            const count = await getUnreadSosCount();

            if (count > prevCountRef.current) {
                setShowSnackbar(true);
            }

            prevCountRef.current = count;
            setUnreadCount(count);
        };

        load();
        const interval = setInterval(load, 300000);
        return () => clearInterval(interval);
    }, [activeMenuItem]);

    useEffect(() => {
        const activeTrail = (data: MainMenuItemInterface[], value: string) => {
            let tree: string[] = [];
            for (const item of data) {
                if (item.children?.length > 0) {
                    tree = activeTrail(item.children, value);
                    if (tree[tree?.length - 1] === value) {
                        tree.push(item.id);
                        return tree;
                    }
                }
                if (item.id === value) {
                    tree.push(item.id);
                    return tree;
                }
            }
            return tree;
        };

        setActiveTrail(activeTrail(menu, activeMenuItem) || []);
    }, [activeMenuItem, menu, setActiveTrail]);

    return (
        <StyledDrawer
            variant={'permanent'}
            className={open ? 'MuiDrawer-open' : 'MuiDrawer-close'}
            classes={{
                paper: (open ? 'MuiPaper-open' : 'MuiPaper-close'),
            }}
        >
            {menu?.length ? (
                <StyledScrollBars
                    style={{ height: "calc(100vh - 136px)", overflow: "hidden" }}
                    hideTracksWhenNotNeeded={true}
                >
                    <div style={{ marginRight: "inherit" }}>
                        {menu?.map((item) => (
                            <IOPDrawerWrapper
                                unreadCount={unreadCount}
                                key={item.id}
                                drawerItem={item}
                                depth={0}
                                drawerOpen={open}
                                handleDrawerToggle={handleDrawerToggle}
                            />
                        ))}
                    </div>
                </StyledScrollBars>
            ) : (
                <ListItem>
                    <CircularProgress color="secondary" />
                </ListItem>
            )}

            <Divider />

            <StyledToolbar
                className={open ? 'MuiToolbar-open' : 'MuiToolbar-close'}
                ref={toolbarRef}
            >
                <StyledUserLinkWrapper>
                    <UserLink />
                </StyledUserLinkWrapper>

                <StyledToggleButton
                    onClick={() => handleDrawerToggle(!open)}
                >
                    {open ? (
                        <ChevronLeftIcon fontSize="large" />
                    ) : (
                        <ChevronRightIcon fontSize="large" />
                    )}
                </StyledToggleButton>
            </StyledToolbar>

            

			<Snackbar
				open={showSnackbar}
				autoHideDuration={30000}
				onClose={(event) => {
					setShowSnackbar(false);
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{
					position: "fixed",
					top: "0 !important",
					left: "50%",
					transform: "translateX(-50%)",
					width: "35%",
					maxWidth: "80%",
				}}
			>
				<Alert
					severity="info"
					onClose={(event) => {
						event.stopPropagation(); 
						setShowSnackbar(false);
					}}
					onClick={() => {
						navigate("/");
						setShowSnackbar(false);
					}}
					sx={{
						width: "100%",
						borderRadius: 0,
						justifyContent: "center",
						cursor: "pointer",
					}}
				>
					Nieuwe SOS melding beschikbaar
				</Alert>
			</Snackbar>


        </StyledDrawer>
    );
};

const drawerWidth = 400;

const StyledScrollBars = styled(Scrollbars)(({ theme }) => ({
    height: "100%",
    "& > div:first-of-type": {
      overflow: "auto !important",
      marginRight: "0px !important",
      scrollbarWidth: "none",
      "-ms-overflow-style": "none",
    },
    "& > div:nth-of-type(2)": {
      display: "none",
    },
    "& > div:nth-of-type(3)": {
      left: "0",
    },
}));

const StyledDrawer = styled(Drawer)(() => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&.MuiDrawer-close, .MuiPaper-close': {
        display: "flex",
        flexDirection: "column",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: "hidden",
        width: parseInt(theme.spacing(9)) + 1,
    },
    '&.MuiDrawer-open, .MuiPaper-open': {
        justifyContent: "space-between",
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
}));

const StyledToolbar = styled('div')(() => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
    minHeight: '122px',
    position: 'fixed',
    bottom: '0',
    width: drawerWidth,
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
    '&.MuiToolbar-open': {
        width: drawerWidth,
    },
    '&.MuiToolbar-close': {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: parseInt(theme.spacing(9)) + 1,
    },
}));

const StyledUserLinkWrapper = styled('div')({
    width: "100%",
});

const StyledToggleButton = styled('div')({
    cursor: 'pointer',
    margin: "4px",
    padding: "0",
});

const mapStateToProps = (state: { activeMenuItem: any; menu: any }) => {
    return {
        activeMenuItem: state.activeMenuItem,
        menu: state.menu,
    };
};

const mapDispatchToProps = {
    fetchMenu: fetchMenuAction,
    setActiveTrail: setActiveTrailAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(IOPDrawer);
